<template>
  <main class="adm-page">

    <!-- Back -->
    <router-link to="/systeme/admin/formations" class="adm-back-link"><ChevronLeft />Retour aux formations</router-link>

    <div class="adm-header">
      <div>
        <h1 class="adm-header__title">Inscriptions</h1>
        <p class="adm-header__sub" v-if="formation">{{ formation.title }} — {{ pagination.total }} inscrits</p>
      </div>
      <div class="adm-header__actions">
        <button class="adm-btn adm-btn--outline" :disabled="exportLoading" @click="handleExport">
          <span v-if="exportLoading" class="adm-ring adm-ring--sm"></span>
          <Download v-else />
          Exporter CSV
        </button>
      </div>
    </div>

    <!-- KPIs -->
    <div class="adm-kpis">
      <div class="adm-kpi adm-kpi--forest"><div class="adm-kpi__icon"><Users /></div><span class="adm-kpi__val">{{ registrationStats.total||0 }}</span><span class="adm-kpi__lbl">Total</span></div>
      <div class="adm-kpi adm-kpi--amber"><div class="adm-kpi__icon"><Clock /></div><span class="adm-kpi__val">{{ registrationStats.en_attente||0 }}</span><span class="adm-kpi__lbl">En attente</span></div>
      <div class="adm-kpi adm-kpi--sage"><div class="adm-kpi__icon"><CheckCircle /></div><span class="adm-kpi__val">{{ registrationStats.confirmés||0 }}</span><span class="adm-kpi__lbl">Confirmés</span></div>
      <div class="adm-kpi" style="background:var(--white);border:1.5px solid var(--ink-12)">
        <div class="adm-kpi__icon" style="background:var(--cream)"><Award /></div>
        <span class="adm-kpi__val">{{ registrationStats.taux_occupation||0 }}%</span>
        <span class="adm-kpi__lbl">Occupation ({{ registrationStats.confirmés||0 }}/{{ registrationStats.capacite||0 }})</span>
        <div class="adm-progress-track" style="margin-top:.4rem"><div class="adm-progress-fill" :style="{width:(registrationStats.taux_occupation||0)+'%'}"></div></div>
      </div>
    </div>

    <!-- Filtres -->
    <div class="adm-filters">
      <div class="adm-search" style="flex:1;min-width:200px"><Search /><input v-model="filters.search" @input="debouncedSearch" placeholder="Nom, email…" class="adm-input" /></div>
      <select v-model="filters.status" @change="applyFilters" class="adm-input adm-input--sm">
        <option value="">Tous statuts</option><option value="en_attente">En attente</option><option value="confirmé">Confirmé</option><option value="annulé">Annulé</option><option value="terminé">Terminé</option>
      </select>
      <select v-model="filters.payment_status" @change="applyFilters" class="adm-input adm-input--sm">
        <option value="">Paiement</option><option value="pending">En attente</option><option value="paid">Payé</option><option value="refunded">Remboursé</option>
      </select>
      <input v-model="filters.date_from" type="date" class="adm-input adm-input--sm" @change="applyFilters" />
      <input v-model="filters.date_to"   type="date" class="adm-input adm-input--sm" @change="applyFilters" />
      <button class="adm-btn adm-btn--outline adm-btn--sm" @click="resetFilters"><X />Réinitialiser</button>
    </div>

    <!-- Table -->
    <div class="adm-card">
      <div v-if="loading" class="adm-loading"><span class="adm-ring"></span></div>
      <div v-else>
        <div class="adm-table-wrap">
          <table class="adm-table">
            <thead>
              <tr>
                <th><button @click="sortBy('first_name')">Candidat</button></th>
                <th>Contact</th>
                <th><button @click="sortBy('status')">Statut</button></th>
                <th>Paiement</th>
                <th><button @click="sortBy('registered_at')">Inscrit le</button></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!registrations.length"><td colspan="6" class="adm-table__empty">Aucune inscription trouvée</td></tr>
              <tr v-for="r in registrations" :key="r.id">
                <td data-label="Candidat">
                  <div style="display:flex;align-items:center;gap:.65rem">
                    <div class="adm-avatar" style="width:36px;height:36px;font-size:.8rem">{{ (r.first_name?.[0]||'')+(r.last_name?.[0]||'') }}</div>
                    <div>
                      <div class="adm-cell-title" style="max-width:none">{{ r.first_name }} {{ r.last_name }}</div>
                      <div class="adm-cell-sub">{{ r.email }}</div>
                    </div>
                  </div>
                </td>
                <td data-label="Contact">
                  <span class="adm-chip"><Phone style="width:11px;height:11px" />{{ r.phone||'—' }}</span>
                </td>
                <td data-label="Statut">
                  <span class="adm-badge" :class="{
                    'adm-badge--amber':  r.status==='en_attente',
                    'adm-badge--green':  r.status==='confirmé',
                    'adm-badge--red':    r.status==='annulé',
                    'adm-badge--forest': r.status==='terminé'
                  }">{{ r.status }}</span>
                </td>
                <td data-label="Paiement">
                  <span class="adm-badge" :class="{
                    'adm-badge--amber': r.payment_status==='pending',
                    'adm-badge--green': r.payment_status==='paid',
                    'adm-badge--gray':  r.payment_status==='refunded'||!r.payment_status
                  }">{{ r.payment_status||'—' }}</span>
                </td>
                <td data-label="Inscrit le" class="adm-cell-date">{{ fmtDate(r.registered_at) }}</td>
                <td class="no-label">
                  <div class="adm-actions">
                    <button class="adm-btn adm-btn--outline adm-btn--xs" @click="viewRegistration(r)" title="Voir"><Eye /></button>
                    <div class="adm-drop">
                      <button class="adm-btn adm-btn--outline adm-btn--xs" @click.stop="toggleActionsMenu(r.id)"><MoreVertical /></button>
                      <div v-if="activeActionMenu===r.id" class="adm-drop__menu">
                        <button v-if="r.status==='en_attente'" class="adm-drop__item" @click="doConfirm(r)"><CheckCircle />Confirmer</button>
                        <button v-if="r.status!=='terminé'" class="adm-drop__item" @click="doComplete(r)"><Award />Marquer terminé</button>
                        <button v-if="r.status!=='annulé'" class="adm-drop__item adm-drop__item--red" @click="doCancel(r)"><XCircle />Annuler</button>
                        <button class="adm-drop__item" @click="editNotes(r)"><Edit />Notes</button>
                        <button class="adm-drop__item adm-drop__item--red" @click="confirmDelete(r)"><Trash2 />Supprimer</button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="pagination.last_page > 1" class="adm-pagination">
          <p class="adm-pagination__info">{{ pagination.total }} inscriptions</p>
          <div class="adm-pagination__pages">
            <button class="adm-page-btn" :disabled="pagination.current_page===1" @click="changePage(pagination.current_page-1)"><ChevronLeft /></button>
            <button v-for="p in displayedPages" :key="p" class="adm-page-btn" :class="{'adm-page-btn--active':pagination.current_page===p}" @click="changePage(p)">{{ p }}</button>
            <button class="adm-page-btn" :disabled="pagination.current_page===pagination.last_page" @click="changePage(pagination.current_page+1)"><ChevronRight /></button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Détail -->
    <Transition name="adm-modal">
      <div v-if="showDetailsModal && selectedRegistration" class="adm-modal" @click.self="closeDetailsModal">
        <div class="adm-modal__box adm-modal__box--lg">
          <div class="adm-modal__head"><h3 class="adm-modal__title">{{ selectedRegistration.first_name }} {{ selectedRegistration.last_name }}</h3><button class="adm-modal__close" @click="closeDetailsModal"><X /></button></div>
          <div class="adm-modal__body">
            <div class="adm-detail-grid">
              <div class="adm-detail-row"><p class="adm-detail-row__label">Email</p><p class="adm-detail-row__val">{{ selectedRegistration.email }}</p></div>
              <div class="adm-detail-row"><p class="adm-detail-row__label">Téléphone</p><p class="adm-detail-row__val">{{ selectedRegistration.phone||'—' }}</p></div>
              <div class="adm-detail-row"><p class="adm-detail-row__label">Statut</p><p class="adm-detail-row__val"><span class="adm-badge" :class="{'adm-badge--green':selectedRegistration.status==='confirmé','adm-badge--amber':selectedRegistration.status==='en_attente'}">{{ selectedRegistration.status }}</span></p></div>
              <div class="adm-detail-row"><p class="adm-detail-row__label">Paiement</p><p class="adm-detail-row__val">{{ selectedRegistration.payment_status||'—' }}</p></div>
              <div class="adm-detail-row"><p class="adm-detail-row__label">Inscrit le</p><p class="adm-detail-row__val">{{ fmtDate(selectedRegistration.registered_at) }}</p></div>
              <div class="adm-detail-row"><p class="adm-detail-row__label">Montant payé</p><p class="adm-detail-row__val">{{ selectedRegistration.amount_paid ? selectedRegistration.amount_paid + ' €' : '—' }}</p></div>
            </div>
            <div v-if="selectedRegistration.motivation" class="adm-field" style="margin-top:.75rem"><label class="adm-label">Motivation</label><div style="background:var(--cream);border-radius:6px;padding:1rem;font-size:.87rem;color:var(--ink-60);line-height:1.65 ;word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;">{{ selectedRegistration.motivation }}</div></div>
            <div v-if="selectedRegistration.admin_notes" class="adm-field" style="margin-top:.75rem"><label class="adm-label">Notes admin</label><div style="background:var(--cream-d);border-radius:6px;padding:1rem;font-size:.87rem;color:var(--ink);line-height:1.65; word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;">{{ selectedRegistration.admin_notes }}</div></div>
          </div>
          <div class="adm-modal__foot">
            <button class="adm-btn adm-btn--outline" @click="closeDetailsModal">Fermer</button>
            <button v-if="selectedRegistration.status==='en_attente'" class="adm-btn adm-btn--green" @click="doConfirm(selectedRegistration);closeDetailsModal()"><CheckCircle />Confirmer</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Modal Notes -->
    <Transition name="adm-modal">
      <div v-if="showNotesModal" class="adm-modal" @click.self="showNotesModal=false">
        <div class="adm-modal__box">
          <div class="adm-modal__head"><h3 class="adm-modal__title">Notes pour {{ notesRegistration?.first_name }}</h3><button class="adm-modal__close" @click="showNotesModal=false"><X /></button></div>
          <div class="adm-modal__body">
            <div class="adm-field">
              <label class="adm-label">Notes internes (visibles uniquement par les admins)</label>
              <textarea v-model="notesText" rows="5" class="adm-input adm-textarea" placeholder="Observations, commentaires…"></textarea>
            </div>
          </div>
          <div class="adm-modal__foot">
            <button class="adm-btn adm-btn--outline" @click="showNotesModal=false">Annuler</button>
            <button class="adm-btn adm-btn--terra" @click="saveNotes">Enregistrer</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Delete confirm -->
    <Transition name="adm-modal">
      <div v-if="showDeleteConfirm" class="adm-modal" @click.self="showDeleteConfirm=false">
        <div class="adm-modal__box">
          <div class="adm-modal__head"><h3 class="adm-modal__title">Supprimer l'inscription ?</h3><button class="adm-modal__close" @click="showDeleteConfirm=false"><X /></button></div>
          <div class="adm-modal__body"><div class="adm-confirm"><div class="adm-confirm__icon"><Trash2 /></div><h3>Confirmer</h3><p>{{ registrationToDelete?.first_name }} {{ registrationToDelete?.last_name }} sera supprimé.</p></div></div>
          <div class="adm-modal__foot">
            <button class="adm-btn adm-btn--outline" @click="showDeleteConfirm=false">Annuler</button>
            <button class="adm-btn adm-btn--red" @click="deleteRegistration"><Trash2 />Supprimer</button>
          </div>
        </div>
      </div>
    </Transition>

    <Transition name="adm-toast"><div v-if="toastMessage" class="adm-toast"><CheckCircle />{{ toastMessage }}</div></Transition>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { debounce } from 'lodash'
import { ChevronLeft, ChevronRight, Download, Search, X, MoreVertical, Eye, Edit, Trash2,
  CheckCircle, XCircle, Award, Users, Phone, Clock } from 'lucide-vue-next'

export default {
  name: 'AdminFormationRegistrations',
  components: { ChevronLeft, ChevronRight, Download, Search, X, MoreVertical, Eye, Edit, Trash2, CheckCircle, XCircle, Award, Users, Phone, Clock },
  data() {
    return {
      formationId:null, activeActionMenu:null,
      showDetailsModal:false, showNotesModal:false, showDeleteConfirm:false,
      selectedRegistration:null, notesRegistration:null, registrationToDelete:null,
      notesText:'', displayedPages:[], toastMessage:'', exportLoading:false,
      filters:{search:'',status:'',payment_status:'',date_from:'',date_to:'',sort_by:'registered_at',sort_order:'desc'}
    }
  },
  computed: {
    ...mapState({loading:s=>s.loading,registrations:s=>s.formationRegistrations,formation:s=>s.currentFormation,pagination:s=>s.formationRegistrationsPagination,registrationStats:s=>s.registrationStats})
  },
  watch: {
    'pagination.last_page':{handler(){this.updateDisplayedPages()},immediate:true},
    'pagination.current_page':{handler(){this.updateDisplayedPages()}},
    toastMessage:{handler(v){if(v)setTimeout(()=>{this.toastMessage=''},3000)}}
  },
  created(){this.formationId=this.$route.params.id;this.debouncedSearch=debounce(this.applyFilters,500);this.loadData() ;console.log(this.formationId)},
  methods: {
    ...mapActions(['fetchFormationRegistrations','fetchRegistrationStats','updateFormationRegistration','deleteFormationRegistration','confirmRegistration','cancelRegistration','completeRegistration','exportRegistrations','setRegistrationFilter','setRegistrationPage','resetRegistrationFilters']),
    async loadData(){  console.log(this.formationId);try{await Promise.all([this.fetchFormationRegistrations({formationId:this.formationId}),this.fetchRegistrationStats(this.formationId)])}catch(e){console.error(e);this.toastMessage='Erreur lors du chargement'}},
    applyFilters(){this.setRegistrationPage(1);this.fetchFormationRegistrations({formationId:this.formationId,params:this.filters})},
    sortBy(f){let o=this.filters.sort_by===f?(this.filters.sort_order==='asc'?'desc':'asc'):'asc';this.filters.sort_by=f;this.filters.sort_order=o;this.applyFilters()},
    resetFilters(){this.filters={search:'',status:'',payment_status:'',date_from:'',date_to:'',sort_by:'registered_at',sort_order:'desc'};this.resetRegistrationFilters();this.fetchFormationRegistrations({formationId:this.formationId})},
    changePage(p){if(p>=1&&p<=this.pagination.last_page){this.setRegistrationPage(p);this.fetchFormationRegistrations({formationId:this.formationId})}},
    updateDisplayedPages(){const c=this.pagination.current_page,l=this.pagination.last_page,d=2,pages=[];for(let i=1;i<=l;i++){if(i===1||i===l||(i>=c-d&&i<=c+d))pages.push(i)}this.displayedPages=pages},
    viewRegistration(r){this.selectedRegistration={...r};this.showDetailsModal=true;this.activeActionMenu=null},
    closeDetailsModal(){this.showDetailsModal=false;this.selectedRegistration=null},
    editNotes(r){this.notesRegistration=r;this.notesText=r.admin_notes||'';this.showNotesModal=true;this.activeActionMenu=null},
    async saveNotes(){if(this.notesRegistration){try{await this.updateFormationRegistration({id:this.notesRegistration.id,data:{admin_notes:this.notesText}});this.toastMessage='Notes enregistrées';this.showNotesModal=false;this.loadData()}catch(e){console.error(e)}}},
    async doConfirm(r){try{await this.confirmRegistration(r.id);this.toastMessage='Inscription confirmée';this.loadData();this.activeActionMenu=null}catch(e){console.error(e)}},
    async doCancel(r){try{await this.cancelRegistration(r.id);this.toastMessage='Inscription annulée';this.loadData();this.activeActionMenu=null}catch(e){console.error(e)}},
    async doComplete(r){try{await this.completeRegistration(r.id);this.toastMessage='Marqué comme terminé';this.loadData();this.activeActionMenu=null}catch(e){console.error(e)}},
    confirmDelete(r){this.registrationToDelete=r;this.showDeleteConfirm=true;this.activeActionMenu=null},
    async deleteRegistration(){if(this.registrationToDelete){try{await this.deleteFormationRegistration(this.registrationToDelete.id);this.toastMessage='Inscription supprimée';this.showDeleteConfirm=false;this.registrationToDelete=null;this.loadData()}catch(e){console.error(e)}}},
    async handleExport(){this.exportLoading=true;try{await this.exportRegistrations(this.formationId);this.toastMessage='Export téléchargé'}catch(e){console.error(e)}finally{this.exportLoading=false}},
    toggleActionsMenu(id){this.activeActionMenu=this.activeActionMenu===id?null:id},
    fmtDate(d){if(!d)return'—';return new Date(d).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit',year:'numeric'})}
  },
  mounted(){document.addEventListener('click',e=>{if(!e.target.closest('.adm-drop'))this.activeActionMenu=null})}
}
</script>
<style>@import './admin.css';</style>
<style scoped>
.adm-back-link{display:inline-flex;align-items:center;gap:.35rem;font-size:.85rem;font-weight:500;color:var(--terra);text-decoration:none;margin-bottom:.5rem;transition:gap .2s ease}
.adm-back-link:hover{gap:.6rem}
.adm-back-link svg{width:16px;height:16px}
</style>
